import { apiFetch } from '../utils/api';
import { VehicleBrand } from './vehicleBrands';
import { VehicleModel } from './vehicleModels';

type ChecklistInputType =
  | 'boolean'
  | 'text'
  | 'single_select'
  | 'multi_select';

export type ChecklistType = 'inspection' | 'pre_delivery';

export interface InquiryChecklist {
  id: string;
  name: string;
  description: string | null;
  inputType: ChecklistInputType;
  answerOptions: ({ answer: string; bgColor?: string } | string)[] | null;
  type: ChecklistType;
  isMandatory: boolean;
  createdAt: string;
  updatedAt: string;
  brands?: VehicleBrand[];
  models?: VehicleModel[];
}

export interface PaginatedInquiryChecklistsResponse {
  data: InquiryChecklist[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface FetchInquiryChecklistsParams {
  page?: number;
  limit?: number;
  search?: string;
  brandId?: string;
  modelId?: string;
  includeGeneral?: boolean;
  type?: ChecklistType;
  isMandatory?: boolean;
}

export const fetchInquiryChecklists = (
  params: FetchInquiryChecklistsParams = {},
): Promise<PaginatedInquiryChecklistsResponse> => {
  const searchParams = new URLSearchParams();

  if (params.page) {
    searchParams.set('page', String(params.page));
  }

  if (params.limit) {
    searchParams.set('limit', String(params.limit));
  }

  if (params.search) {
    searchParams.set('search', params.search);
  }

  if (params.brandId) {
    searchParams.set('brandId', params.brandId);
  }

  if (params.modelId) {
    searchParams.set('modelId', params.modelId);
  }

  if (params.includeGeneral !== undefined) {
    searchParams.set('includeGeneral', String(params.includeGeneral));
  }

  if (params.type) {
    searchParams.set('type', params.type);
  }

  if (params.isMandatory !== undefined) {
    searchParams.set('isMandatory', String(params.isMandatory));
  }

  const query = searchParams.toString();
  return apiFetch<PaginatedInquiryChecklistsResponse>(
    `/inquiry/checklists${query ? `?${query}` : ''}`,
  );
};

interface ChecklistPayload {
  name: string;
  description?: string;
  inputType: ChecklistInputType;
  answerOptions?: ({ answer: string; bgColor?: string } | string)[];
  brandIds?: string[];
  modelIds?: string[];
  type: ChecklistType;
  isMandatory?: boolean;
}

export const createInquiryChecklist = (
  payload: ChecklistPayload,
): Promise<InquiryChecklist> =>
  apiFetch<InquiryChecklist>('/inquiry/checklists', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateInquiryChecklist = (
  id: string,
  payload: Partial<ChecklistPayload>,
): Promise<InquiryChecklist> =>
  apiFetch<InquiryChecklist>(`/inquiry/checklists/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const deleteInquiryChecklist = (id: string): Promise<void> =>
  apiFetch<void>(`/inquiry/checklists/${id}`, {
    method: 'DELETE',
  });

export type { ChecklistInputType };

export const fetchInquiryChecklist = (id: string): Promise<InquiryChecklist> =>
  apiFetch<InquiryChecklist>(`/inquiry/checklists/${id}`);
